import React from 'react';
import {
  MdCheckCircle,
  MdCloudDownload,
  MdCloudOff,
  MdErrorOutline,
  MdSync,
  MdWarningAmber,
} from 'react-icons/md';
import {
  BUTTON_SIZE_SM,
  BUTTON_VARIANT_TEXT,
  Button,
  STATUS_PILL_TONE_CAUTION,
  STATUS_PILL_TONE_ERROR,
  STATUS_PILL_TONE_NEUTRAL,
  STATUS_PILL_TONE_SUCCESS,
  StatusPill,
  type StatusPillTone,
} from '@aibuild/ui';
import type { AutoSaveStatus } from '../useProcessAutoSave';
import { Label, SpinnerIcon, Wrapper } from './SaveStatusIndicator.styled';

export interface SaveStatusIndicatorProps {
  status: AutoSaveStatus;
  /** Server-side updatedAt of the last successful save. Surfaced in the title. */
  lastSavedAt: string | null;
  /** Backend error message, if any. Used as the title for `error` / `drift`. */
  errorMessage?: string | null;
  /**
   * Number of validation errors on the most recently persisted graph
   * (i.e. {@code detail.validation.errors.length}). When > 0 and the
   * indicator would otherwise render the green "saved" state we instead
   * render an amber "Saved · N issue(s)" pill so the user is aware the
   * graph is persisted but not runnable. Save-lifecycle states (saving,
   * error, drift) take priority and ignore this count.
   */
  validationErrorCount?: number;
  /**
   * Wired to the snackbar Refresh action when status is `drift`. The page
   * should call `queryClient.invalidateQueries(processDetailQueryKey(...))`
   * inside this handler so the editor reloads from the server.
   */
  onRefresh?: () => void;
  /**
   * A conditional GET for external changes is in flight. Presentation only —
   * kept off {@link AutoSaveStatus} so a background sync can never disturb
   * the save state machine.
   */
  isSyncing?: boolean;
  /**
   * The server holds a version we could not apply because the canvas has
   * unsaved edits. Offers the same Refresh action as `drift`, before the
   * user's next save turns it into a real 409.
   */
  changesWaiting?: boolean;
  dataTestId?: string;
}

/**
 * Compact save-status pill rendered next to the editor's runnability chip.
 *
 * Visually identical to the breadcrumb "Draft" pill and the "Not runnable"
 * chip — uses the shared {@link StatusPill} atom so all three indicators
 * line up in the same tone-coloured pill style. (An earlier iteration was
 * a bare text + icon pair on a transparent background; against the topbar
 * surface that made the indicator effectively invisible and users reported
 * "I never see the save status pill at all".)
 *
 * Surfaces the {@link AutoSaveStatus} state machine:
 *   - `idle` / `saved`         → "All changes saved" (success / neutral pill)
 *   - `saving`                 → "Saving…" (neutral pill, spinner icon)
 *   - `error`                  → "Save failed" (error pill)
 *   - `drift`                  → "Out of sync" (caution pill) + Refresh button
 *
 * Plus two faces that belong to the background canvas sync rather than to
 * {@link AutoSaveStatus}: "Syncing changes…" and "Changes available" (which
 * offers the same Refresh action). Both only show while the save machine is
 * quiescent — a save in flight or a live conflict is the more urgent signal.
 *
 * If the persisted graph has validation issues ({@code validationErrorCount > 0}),
 * the otherwise-green "saved" / "all changes saved" labels are replaced with
 * an amber "Saved · N issue(s)" caution pill so the user is reminded that
 * even though their edits are safely persisted the graph still isn't
 * runnable. The runnability chip next to this indicator carries the same
 * signal in more detail; the pill intentionally duplicates it because the
 * chip is easy to miss when the user is focused on save status.
 *
 * The transient `saved` state is left visible for ~1.5s by the hook before
 * fading back to `idle`; this component just renders whatever status it's
 * given without timing concerns.
 */
const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({
  status,
  lastSavedAt,
  errorMessage,
  validationErrorCount = 0,
  onRefresh,
  isSyncing = false,
  changesWaiting = false,
  dataTestId = 'save-status-indicator',
}) => {
  const presentation = presentationFor(
    status,
    isSyncing,
    changesWaiting,
    validationErrorCount,
  );
  // Pre-first-edit guard. We only suppress the pill when there's truly
  // nothing to show — the runnability chip carries the validation signal
  // on its own until the first save sets a baseline.
  if (status === 'idle' && !lastSavedAt && presentation === 'save-status') {
    return null;
  }
  const tone = toneForPresentation(presentation, status);
  const title =
    presentation === 'changes-waiting'
      ? 'This process changed elsewhere. Save or discard your edits, then refresh to load it.'
      : titleFor(status, lastSavedAt, errorMessage, validationErrorCount);
  const showRefresh =
    (status === 'drift' || presentation === 'changes-waiting') &&
    Boolean(onRefresh);
  return (
    <Wrapper
      data-testid={dataTestId}
      data-status={status}
      data-tone={tone}
      data-presentation={presentation}
      title={title}
    >
      <StatusPill
        tone={tone}
        icon={iconForPresentation(presentation, status)}
        dataTestId={`${dataTestId}__pill`}
      >
        <Label data-testid={`${dataTestId}__label`}>
          {labelForPresentation(presentation, status, validationErrorCount)}
        </Label>
      </StatusPill>
      {showRefresh ? (
        <Button
          variant={BUTTON_VARIANT_TEXT}
          size={BUTTON_SIZE_SM}
          onClick={onRefresh}
          dataTestId={`${dataTestId}__refresh`}
        >
          {presentation === 'changes-waiting' ? 'Discard & refresh' : 'Refresh'}
        </Button>
      ) : null}
    </Wrapper>
  );
};

/**
 * Which of the pill's four faces to render.
 *
 * The save-lifecycle states outrank the sync ones: `saving`, `error` and
 * `drift` are all about the user's own edits, whereas a sync is background
 * reconciliation the user did not ask for.
 */
type Presentation =
  | 'save-status'
  | 'sync-in-flight'
  | 'changes-waiting'
  | 'validation-warning';

const presentationFor = (
  status: AutoSaveStatus,
  isSyncing: boolean,
  changesWaiting: boolean,
  validationErrorCount: number,
): Presentation => {
  if (!isQuiescent(status)) return 'save-status';
  if (isSyncing) return 'sync-in-flight';
  if (changesWaiting) return 'changes-waiting';
  if (validationErrorCount > 0) return 'validation-warning';
  return 'save-status';
};

const toneForPresentation = (
  presentation: Presentation,
  status: AutoSaveStatus,
): StatusPillTone => {
  switch (presentation) {
    case 'sync-in-flight':
      return STATUS_PILL_TONE_NEUTRAL;
    case 'changes-waiting':
    case 'validation-warning':
      return STATUS_PILL_TONE_CAUTION;
    case 'save-status':
      return toneFor(status);
    default: {
      const exhaustive: never = presentation;
      return exhaustive;
    }
  }
};

const labelForPresentation = (
  presentation: Presentation,
  status: AutoSaveStatus,
  validationErrorCount: number,
): string => {
  switch (presentation) {
    case 'sync-in-flight':
      return 'Syncing changes…';
    case 'changes-waiting':
      return 'Changes available';
    case 'validation-warning':
      return validationLabel(validationErrorCount);
    case 'save-status':
      return labelFor(status);
    default: {
      const exhaustive: never = presentation;
      return exhaustive;
    }
  }
};

const iconForPresentation = (
  presentation: Presentation,
  status: AutoSaveStatus,
): React.ReactNode => {
  switch (presentation) {
    case 'sync-in-flight':
      return (
        <SpinnerIcon aria-hidden>
          <MdSync size={14} />
        </SpinnerIcon>
      );
    case 'changes-waiting':
      return <MdCloudDownload size={14} />;
    case 'validation-warning':
      return <MdWarningAmber size={14} />;
    case 'save-status':
      return iconFor(status);
    default: {
      const exhaustive: never = presentation;
      return exhaustive;
    }
  }
};

/**
 * The validation warning is only meaningful when the user is in a
 * "settled" state — they've successfully saved (saved / post-baseline
 * idle) and the persisted graph carries issues. Mid-flight states
 * (saving) and failure states (error / drift) take priority because
 * their messaging is more urgent and the validation count reflects an
 * older snapshot anyway.
 */
const isQuiescent = (status: AutoSaveStatus): boolean =>
  status === 'saved' || status === 'idle';

const toneFor = (status: AutoSaveStatus): StatusPillTone => {
  switch (status) {
    case 'saved':
      return STATUS_PILL_TONE_SUCCESS;
    case 'error':
      return STATUS_PILL_TONE_ERROR;
    case 'drift':
      return STATUS_PILL_TONE_CAUTION;
    case 'saving':
    case 'idle':
    default:
      return STATUS_PILL_TONE_NEUTRAL;
  }
};

const labelFor = (status: AutoSaveStatus): string => {
  switch (status) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'error':
      return 'Save failed';
    case 'drift':
      return 'Out of sync';
    case 'idle':
    default:
      return 'All changes saved';
  }
};

const validationLabel = (count: number): string =>
  count === 1 ? 'Saved · 1 issue' : `Saved · ${count} issues`;

const titleFor = (
  status: AutoSaveStatus,
  lastSavedAt: string | null,
  errorMessage: string | null | undefined,
  validationErrorCount: number,
): string => {
  if ((status === 'error' || status === 'drift') && errorMessage) {
    return errorMessage;
  }
  const base = lastSavedAt
    ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString()}`
    : 'No saves yet';
  if (isQuiescent(status) && validationErrorCount > 0) {
    const noun = validationErrorCount === 1 ? 'issue' : 'issues';
    return `${base} — graph has ${validationErrorCount} validation ${noun}`;
  }
  return base;
};

const iconFor = (status: AutoSaveStatus): React.ReactNode => {
  switch (status) {
    case 'saving':
      return (
        <SpinnerIcon aria-hidden>
          <MdSync size={14} />
        </SpinnerIcon>
      );
    case 'saved':
      return <MdCheckCircle size={14} />;
    case 'error':
      return <MdErrorOutline size={14} />;
    case 'drift':
      return <MdCloudOff size={14} />;
    case 'idle':
    default:
      return <MdCheckCircle size={14} />;
  }
};

// Re-export the refresh glyph so call-sites that need the icon for a
// custom renderer (e.g. unit tests checking the snackbar) can grab it
// without duplicating the registry.
export { MdRefresh as SaveStatusRefreshIcon } from 'react-icons/md';

export default SaveStatusIndicator;
